import type {
  GeoBrowseDegRow,
  GeoBrowseDisease,
  GeoBrowseEnrichmentRow,
  GeoBrowsePcaPoint,
  GeoBrowseSample,
  GeoBrowseStudy,
  GeoBrowseStudyDetail,
} from '../types';

/** Backend DTOs (camelCase) for /api/geo-browse/* */

export type ApiDisease = {
  cui: string;
  cuiName: string;
  nstudiesTotal: number;
  nstudiesUsable: number;
  nstudiesWithDeg: number;
  nsamplesTotal: number;
  ngenesTested?: number;
  ngenesSig?: number;
};

export type ApiStudyDetail = {
  cui: string;
  cuiName?: string;
  accession: string;
  comparisonId?: string;
  title: string;
  summary?: string | null;
  overallDesign?: string | null;
  year: number;
  pubmedids?: string | null;
  platform?: string | null;
  usableForDeg: boolean;
  controlCaveat?: string | null;
  qcFlagRate?: number | null;
  hasDeg: boolean;
  hasGseaCurated?: boolean;
  hasGseaAll?: boolean;
  hasGseaReactome?: boolean;
  hasGseaWikipathways?: boolean;
  nsamples: number;
  ncase: number;
  ncontrol: number;
  ngenesTested?: number;
  ngenesSig?: number;
  qcFlags?: number;
  covariatesInModel?: string | null;
  confidentSexMismatches?: number;
  confidentGroupConsistencyFlags?: number;
};

export type ApiSample = {
  cui?: string;
  accession?: string;
  sampleId: string;
  group?: string | null;
  sex?: string | null;
  sexSource?: string | null;
  tissue?: string | null;
};

export type ApiPcaPoint = {
  cui?: string;
  accession?: string;
  sampleId: string;
  group?: string | null;
  pc1: number;
  pc2: number;
  pctVarPc1: number;
  pctVarPc2: number;
  ngenesUsed?: number;
};

export type ApiDegRow = {
  cui?: string;
  accession?: string;
  comparisonId?: string;
  geneId: string | number;
  geneSymbol: string;
  baseMean: number;
  log2FoldChange: number;
  lfcSE: number;
  stat: number;
  pvalue: number;
  padj: number;
};

export type ApiEnrichmentRow = {
  cui?: string;
  accession?: string;
  comparisonId?: string;
  collection: string;
  geneSetId: string;
  geneSetName: string;
  es: number;
  nes: number;
  nomPval: number;
  fdrQval: number;
  fwerPval?: number;
  tagPct: number;
  genePct: number;
  leadGenes: string;
};

function num(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function str(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  return String(value);
}

export function mapApiDisease(row: ApiDisease): GeoBrowseDisease {
  return {
    cui: row.cui,
    cui_name: row.cuiName,
    n_studies_total: num(row.nstudiesTotal),
    n_studies_usable: num(row.nstudiesUsable),
    n_studies_with_deg: num(row.nstudiesWithDeg),
    n_samples_total: num(row.nsamplesTotal),
    n_genes_tested: num(row.ngenesTested),
    n_genes_sig: num(row.ngenesSig),
  };
}

export type ApiStudy = {
  cui: string;
  cuiName?: string;
  accession: string;
  comparisonId?: string;
  title?: string;
  overallDesign?: string;
  year?: number;
  platform?: string;
  nsamples?: number;
  ncase?: number;
  ncontrol?: number;
  usableForDeg?: boolean;
  hasDeg?: boolean;
  ngenesTested?: number;
  ngenesSig?: number;
  qcFlags?: number;
};

export function mapApiStudy(row: ApiStudy): GeoBrowseStudy {
  return {
    cui: row.cui,
    cui_name: str(row.cuiName),
    accession: row.accession,
    comparison_id: str(row.comparisonId, `${row.cui}__${row.accession}`),
    title: str(row.title),
    overall_design: str(row.overallDesign),
    year: num(row.year),
    platform: str(row.platform),
    n_samples: num(row.nsamples),
    n_case: num(row.ncase),
    n_control: num(row.ncontrol),
    usable_for_deg: row.usableForDeg ?? false,
    has_deg: row.hasDeg ?? false,
    n_genes_tested: num(row.ngenesTested),
    n_genes_sig: num(row.ngenesSig),
    qc_flags: num(row.qcFlags),
  };
}

export function mapApiStudyDetail(row: ApiStudyDetail): GeoBrowseStudyDetail {
  const qcFlagRate = num(row.qcFlagRate);
  const qcFlags = row.qcFlags != null ? num(row.qcFlags) : Math.round(qcFlagRate);

  return {
    cui: row.cui,
    cui_name: str(row.cuiName),
    accession: row.accession,
    comparison_id: str(row.comparisonId, `${row.cui}__${row.accession}`),
    title: str(row.title),
    overall_design: str(row.overallDesign),
    year: num(row.year),
    platform: str(row.platform),
    n_samples: num(row.nsamples),
    n_case: num(row.ncase),
    n_control: num(row.ncontrol),
    usable_for_deg: Boolean(row.usableForDeg),
    has_deg: Boolean(row.hasDeg),
    n_genes_tested: num(row.ngenesTested),
    n_genes_sig: num(row.ngenesSig),
    qc_flags: qcFlags,
    summary: str(row.summary),
    pubmedids: str(row.pubmedids),
    control_caveat: str(row.controlCaveat),
    qc_flag_rate: qcFlagRate,
    has_gsea_curated: Boolean(row.hasGseaCurated),
    has_gsea_all: Boolean(row.hasGseaAll),
    has_gsea_reactome: Boolean(row.hasGseaReactome),
    has_gsea_wikipathways: Boolean(row.hasGseaWikipathways),
    covariates_in_model: str(row.covariatesInModel),
    confident_sex_mismatches: num(row.confidentSexMismatches),
    confident_group_consistency_flags: num(row.confidentGroupConsistencyFlags),
  };
}

export function mapApiSample(row: ApiSample): GeoBrowseSample {
  return {
    sample_id: row.sampleId,
    group: str(row.group),
    sex: str(row.sex),
    sex_source: str(row.sexSource, 'unknown'),
    ...(row.tissue ? { tissue: str(row.tissue) } : {}),
  };
}

export function mapApiPcaPoint(row: ApiPcaPoint): GeoBrowsePcaPoint {
  return {
    sample_id: row.sampleId,
    group: str(row.group),
    pc1: num(row.pc1),
    pc2: num(row.pc2),
    pct_var_pc1: num(row.pctVarPc1),
    pct_var_pc2: num(row.pctVarPc2),
  };
}

export function mapApiDegRow(row: ApiDegRow): GeoBrowseDegRow {
  return {
    gene_id: num(row.geneId),
    gene_symbol: row.geneSymbol,
    comparison_id: row.comparisonId,
    baseMean: num(row.baseMean),
    log2FoldChange: num(row.log2FoldChange),
    lfcSE: num(row.lfcSE),
    stat: num(row.stat),
    pvalue: num(row.pvalue),
    padj: num(row.padj),
  };
}

export function mapApiEnrichmentRow(row: ApiEnrichmentRow): GeoBrowseEnrichmentRow {
  return {
    gene_set_id: row.geneSetId,
    gene_set_name: row.geneSetName,
    collection: row.collection,
    comparison_id: row.comparisonId,
    es: num(row.es),
    nes: num(row.nes),
    nom_pval: num(row.nomPval),
    fdr_qval: num(row.fdrQval),
    tag_pct: num(row.tagPct),
    gene_pct: num(row.genePct),
    lead_genes: str(row.leadGenes),
  };
}
